/* eslint-disable object-curly-newline */
import { useState } from 'preact/hooks';
import PropTypes from 'prop-types';
import { Fragment } from 'preact';
import AppState from '@state';

import { postOrgContract } from '@api/restricted/org-contracts-api';

import useIsoDateFormat from '@hooks/useIsoDateFormat';

import AlertMessage from '@ui-kit/alert/Alert';
import BaseButton from '@ui-kit/buttons/baseButton';
import Box from '@ui-kit/box';
import BreakLine from '@ui-kit/dividers/breakLine';
import Text from '@ui-kit/typography/text';

const undefinedLabel = 'Not Found';
const textValueMargin = '0.5em';

const d = useIsoDateFormat;
function AddedContractDataView({ contractData, contractFile, loadContracts, closeFunction, readOnly }) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const onClickSubmitContractData = () => {
    setIsLoading(true);
    setError(null);

    postOrgContract(AppState.pubOrganization.id.value, contractFile, contractData)
      .then((response) => {
        if (response.status !== 200) {
          setIsLoading(false);
          response.json()
            .then((json) => setError(json.error || response.statusText))
            .catch(() => setError(response.statusText));
        } else {
          loadContracts().then(() => {
            setIsLoading(false);
            closeFunction();
          });
        }
      })
      .catch((err) => {
        setIsLoading(false);
        setError(err.message);
      });
  };

  return (
    <Box>
      {contractData.parties.map((party) => (
        <Fragment>
          <Text mb="0.813em" fontWeight="800">
            {party.isPublisher ? 'Publisher' : 'Writer'}
            {' '}
            Information
          </Text>

          <Box mb="0.875rem" key={party.name}>
            <Text mb={textValueMargin} fontSize="0.813rem">
              Name:
              {' '}
              {party.name || undefinedLabel}
            </Text>
            {party.isPublisher && (
            <Text mb={textValueMargin} fontSize="0.813rem">
              Administrator Status:
              {' '}
              {party.isAdmin ? 'Administrator' : 'Not an Administrator'}
            </Text>
            )}
            {party.alias && (
            <Text mb={textValueMargin} fontSize="0.813rem">
              Alias:
              {' '}
              {party.alias}
            </Text>
            )}
            <Text mb={textValueMargin} fontSize="0.813rem">
              Email:
              {' '}
              {party.email || undefinedLabel}
            </Text>
            <Text mb={textValueMargin} fontSize="0.813rem">
              IPI:
              {' '}
              {party.ipi || undefinedLabel}
            </Text>
            <Text mb={textValueMargin} fontSize="0.813rem">
              PRO:
              {' '}
              {party.pro?.name || undefinedLabel}
            </Text>
            <Text mb={textValueMargin} fontSize="0.813rem">
              Address:
              {' '}
              {party.address || undefinedLabel}
            </Text>
            <Text mb={textValueMargin} fontSize="0.813rem">
              Country:
              {' '}
              {party.country || undefinedLabel}
            </Text>
            {party.selfPublisher?.name && (
              <Text mb={textValueMargin} fontSize="0.813rem">
                Self-Owned Publisher:
                {' '}
                {party.selfPublisher?.name}
              </Text>
            )}
            {/* {writer.selfPublisher?.name && (
            <>
              <Text mb={textValueMargin} fontSize="0.813rem">
                IPI:
                {' '}
                {writer.selfPublisher?.ipi || undefinedLabel}
              </Text>
              <Text mb={textValueMargin} fontSize="0.813rem">
                PRO:
                {' '}
                {writer.selfPublisher?.pro || undefinedLabel}
              </Text>
              <Text mb={textValueMargin} fontSize="0.813rem">
                Address:
                {' '}
                {writer.selfPublisher?.address || undefinedLabel}
              </Text>
              <Text mb={textValueMargin} fontSize="0.813rem">
                Country:
                {' '}
                {writer.selfPublisher?.country || undefinedLabel}
              </Text>
            </>
            )} */}
          </Box>

          <BreakLine mb="1.25em" mt="1.25em" color="var(--panel-info-border)" />
        </Fragment>
      ))}

      <Text mb="0.813em" fontWeight="800">
        Contract Terms
      </Text>

      <Text mb={textValueMargin} fontSize="0.813rem">
        Deal Type:
        {' '}
        {contractData.type || undefinedLabel}
      </Text>

      <Text mb={textValueMargin} fontSize="0.813rem">
        Effective Dates:
        {' '}
        {`${
          contractData.startDate ? d(contractData.startDate) : undefinedLabel
        } - ${contractData.endDate ? d(contractData.endDate) : undefinedLabel}`}
      </Text>

      <Text mb={textValueMargin} fontSize="0.813rem">
        Undivided Ownership Interest:
        {' '}
        {Number.isNaN(contractData.ownershipPercentage) ? undefinedLabel : `${contractData.ownershipPercentage}%`}
      </Text>

      <Text mb={textValueMargin} fontSize="0.813rem">
        Territory:
        {' '}
        {contractData.territory || undefinedLabel}
      </Text>

      <Text mb={textValueMargin} fontSize="0.813rem">
        Delivery Requirement:
        {' '}
        {contractData.deliveryCommitment?.included
          ? `${contractData.deliveryCommitment?.totalCompositions} compositions${contractData.deliveryCommitment?.qualifyingPercentage > 0 && ` of ${contractData.deliveryCommitment?.qualifyingPercentage}% ownership`}`
          : undefinedLabel}
      </Text>

      {contractData.deliveryCommitment?.included && (
      <Text mb={textValueMargin} fontSize="0.813rem">
        Allow Combining Ownership Percentages:
        {' '}
        {contractData.deliveryCommitment?.aggregation?.allowed
          ? `Yes (${contractData.deliveryCommitment?.aggregation?.minimumPercentage > 0
            ? `at least ${contractData.deliveryCommitment?.aggregation?.minimumPercentage}% ownership`
            : 'no minimum ownership'})`
          : 'No'}
      </Text>
      )}

      <BreakLine mb="1.25em" mt="1.25em" color="var(--panel-info-border)" />

      {error && (
      <AlertMessage
        variant="negative"
        message={error}
        mb="1.5em"
        mt="1.5em"
      />
      )}

      {(contractFile && contractData && !readOnly) && (
      <BaseButton
        mb="1.5em"
        btnText="Save and Continue"
        fluid
        isLoading={isLoading}
        onClick={onClickSubmitContractData}
      />
      )}

    </Box>
  );
}

AddedContractDataView.propTypes = {
  contractData: PropTypes.object.isRequired,
  contractFile: PropTypes.object,
  loadContracts: PropTypes.func.isRequired,
  closeFunction: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};

AddedContractDataView.defaultProps = {
  contractFile: null,
  readOnly: false,
};

export default AddedContractDataView;
